import { Injectable } from "@angular/core";
import { HttpClient, HttpParams, HttpRequest, HttpEvent, HttpHeaders } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { DynamicImageSet } from "../model/dynamic-image-set";
import { EnvConfigurationService } from "./config/env-configuration.service";
import { tap } from "rxjs/operators";
import { TestArea } from "../common/test-area";
import { PromoteDynamicDTO, PromoteStaticDTO, StaticPromoteFilter } from "../model/promote-set";
import { PdfexportType } from "../common/pdfexport-type";
import { User } from "../model/user";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  constructor(private http: HttpClient) {}

  getAllDeploymentSets(): Observable<any> {
    return this.http.get(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/test/dis`);
  }

  getAllDeploymentSetsForTest(testArea?: TestArea): Observable<any> {
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/dis`);
  }

  getResolutions(dynamicImageSet: DynamicImageSet, testArea?: TestArea): Observable<any> {
    let parameters = new HttpParams();
    parameters = parameters.append("viewPort", dynamicImageSet.viewport.substring(0, 3));

    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/dis/${dynamicImageSet.id}/resolutions`, {
      params: parameters,
    });
  }

  getLayers(dynamicImageSet: DynamicImageSet, testArea?: TestArea): Observable<any> {
    let parameters = new HttpParams();
    parameters = parameters.append("viewport", dynamicImageSet.viewport.substring(0, 3)).append("driverType", dynamicImageSet.model);

    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/dis/${dynamicImageSet.id}/layers`, {
      params: parameters,
    });
  }

  getPreselectedLayers(dynamicImageSet: DynamicImageSet, testArea?: TestArea): Observable<any> {
    let parameters = new HttpParams();
    parameters = parameters.append("viewport", dynamicImageSet.viewport.substring(0, 3)).append("disId", dynamicImageSet.id);
    if (dynamicImageSet.version) {
      return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/versions/${dynamicImageSet.version}/layers`, {
        params: parameters,
      });
    } else {
      return of([]);
    }
  }

  getTargetNames(dynamicImageSet: DynamicImageSet, testArea?: TestArea): Observable<any> {
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/dis/${dynamicImageSet.id}/targetNames`);
  }

  getImageBasedOnLayers(dynamicImageSet: DynamicImageSet, selectedLayers: string[], view: string, testArea?: TestArea): Observable<Blob> {
    let parameters = new HttpParams();
    parameters = parameters
      .append("viewport", dynamicImageSet.viewport.substring(0, 3))
      .append("driverType", dynamicImageSet.model)
      .append("size", dynamicImageSet.resolution)
      .append("view", view)
      .append("layers", String(selectedLayers));

    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/image/dis/${dynamicImageSet.id}/image`, {
      params: parameters,
      responseType: "blob",
    });
  }

  getImageBasedOnOptions(dynamicImageSet: DynamicImageSet, selectedOptions: string[], view: string, testArea?: TestArea): Observable<Blob> {
    let parameters = new HttpParams();
    parameters = parameters
      .append("versionId", dynamicImageSet.version)
      .append("viewport", dynamicImageSet.viewport.substring(0, 3))
      .append("driverType", dynamicImageSet.model)
      .append("size", dynamicImageSet.resolution)
      .append("view", view);
    const color = selectedOptions.find((o) => o.startsWith("0M"));
    if (color) {
      parameters = parameters.append("color", color);
    }
    const trim = selectedOptions.find((o) => o.startsWith("0P"));
    if (trim) {
      parameters = parameters.append("trim", trim);
    }
    parameters = parameters.append("options", String(selectedOptions.filter((o) => !(o.startsWith("0M") || o.startsWith("0P")))));
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/image/dis/${dynamicImageSet.id}/imageFromOptions`, {
      params: parameters,
      responseType: "blob",
    });
  }

  getStandardOptions(dynamicImageSet: DynamicImageSet, selectedOptions: string[], testArea?: TestArea): Observable<any> {
    let parameters = new HttpParams();
    const color = selectedOptions.find((o) => o.startsWith("0M"));
    if (color) {
      parameters = parameters.append("colour", color);
    }
    const trim = selectedOptions.find((o) => o.startsWith("0P"));
    if (trim) {
      parameters = parameters.append("trim", trim);
    }
    parameters = parameters.append("additionalOptions", String(selectedOptions.filter((o) => !(o.startsWith("0M") || o.startsWith("0P")))));
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/versions/${dynamicImageSet.version}/standards`, {
      params: parameters,
    });
  }

  getVersions(versionId: string, testArea?: TestArea): Observable<any> {
    let parameters = new HttpParams();
    parameters = parameters.append("disId", versionId);
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/versions`, { params: parameters }).pipe(
      tap((results) => {
        results.sort((a, b) => a.versionId.localeCompare(b.versionId));
      })
    );
  }

  getAvailableViews(disId: string, testArea?: TestArea): Observable<any> {
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/dis/${disId}/validateViews`);
  }

  getFeatures(dynamicImageSet: DynamicImageSet, selected: any[], testArea?: TestArea): Observable<any> {
    let parameters = new HttpParams();
    if (Array.isArray(selected) && selected.length) {
      const colour = selected.find((e) => e.startsWith("0M"));
      if (colour) {
        parameters = parameters.append("colour", colour);
      }
      const trim = selected.find((e) => e.startsWith("0P"));
      if (trim) {
        parameters = parameters.append("trim", trim);
      }
      const additionalOptions = selected.filter((e) => e !== colour && e !== trim).join();
      parameters = parameters.append("additionalOptions", additionalOptions);
    }
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/versions/${dynamicImageSet.version}/features`, {
      params: parameters,
    });
  }

  getAllShortStatus(): Observable<any> {
    return this.http.get(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/test/dis/shortStatus`);
  }

  getAllUploadStaticFileInfo(): Observable<any> {
    return this.http.get(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/test/static/status`);
  }

  getLcdvMapping(testArea?: TestArea): Observable<any> {
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/test/lcdvMapping`);
  }

  promote(setToPromoteId: string, source: string, target: string): Observable<string> {
    const promoteDTO = new PromoteDynamicDTO();
    promoteDTO.disId = setToPromoteId;
    promoteDTO.source = source;
    promoteDTO.target = target;

    const headers = new HttpHeaders().set("Content-Type", "application/json");

    const parameters = new HttpParams();
    console.log(`call ${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/dis/${setToPromoteId}/promote`);
    return this.http.post(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/dis/${setToPromoteId}/promote`, promoteDTO, {
      params: parameters,
      headers,
      responseType: "text",
    });
  }

  promoteMeta(setToPromoteId: string, source: string, target: string): Observable<string> {
    console.log(`service promoteMeta ${setToPromoteId} from ${source} to ${target}`);

    const promoteDTO = new PromoteDynamicDTO();
    promoteDTO.disId = setToPromoteId;
    promoteDTO.source = source;
    promoteDTO.target = target;

    const headers = new HttpHeaders().set("Content-Type", "application/json");

    const parameters = new HttpParams();
    console.log(`call ${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/dis/${setToPromoteId}/promoteMetadata`);
    return this.http.post(
      `${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/dis/${setToPromoteId}/promoteMetadata`,
      promoteDTO,
      { params: parameters, headers, responseType: "text" }
    );
  }

  staticPromote(fileName: string, source: string, target: string): Observable<string> {
    const promoteDTO = new PromoteStaticDTO();
    promoteDTO.fileName = fileName;
    promoteDTO.source = source;
    promoteDTO.target = target;

    const headers = new HttpHeaders().set("Content-Type", "application/json");

    const parameters = new HttpParams();
    console.log(`call ${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/static/promote`);
    return this.http.post(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/static/promote`, promoteDTO, {
      params: parameters,
      headers,
      responseType: "text",
    });
  }

  getVersionsCriteria(testArea?: TestArea): Observable<any> {
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/versionsCriteria`);
  }

  getVersionsStaticCriteria(testArea?: TestArea): Observable<any> {
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/versionsStaticCriteria`);
  }

  getStaticOptionImageInfo(versionWildcard: string, testArea?: TestArea): Observable<any> {
    let parameters = new HttpParams();
    parameters = parameters.append("versionWildcard", versionWildcard);
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/static/optionImageInfo`, { params: parameters });
  }

  getStaticCO2Images(explicit: boolean, testArea?: TestArea): Observable<any> {
    let parameters = new HttpParams();
    parameters = parameters.append("explicit", String(explicit));
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/image/static/CO2ClassImages`, { params: parameters });
  }

  getStaticOptionImageInfoBasedOnOptionType(
    optionType: string,
    paramMap: Map<string, string>,
    explicit: boolean,
    testArea?: TestArea
  ): Observable<any> {
    let parameters = new HttpParams();
    paramMap.forEach((value: string, key: string) => {
      parameters = parameters.append(key, value);
    });
    parameters = parameters.append("explicit", String(explicit));
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/image/static/imageInfo/${optionType}`, {
      params: parameters,
    });
  }

  getPDF(pdfExportType: PdfexportType, testArea?: TestArea): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set("Accept", "application/pdf");
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/pdf/${pdfExportType.toLowerCase()}`, {
      headers: headers,
      responseType: "blob",
      observe: "response",
    });
  }

  getPDFFeatures(pdfExportBrand: string, pdfExportModel: string, testArea?: TestArea): Observable<any> {
    let parameters = new HttpParams();
    parameters = parameters.append("brand", pdfExportBrand);
    parameters = parameters.append("model", pdfExportModel);
    let headers = new HttpHeaders();
    headers = headers.set("Accept", "application/pdf");
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/test/pdf/features`, {
      headers: headers,
      responseType: "blob",
      observe: "response",
      params: parameters,
    });
  }

  getBuildInfo(): Observable<any> {
    return this.http.get(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/public/build-info`);
  }

  getCredentials(): Observable<User> {
    return this.http.get<User>(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/public/user`);
  }

  refreshCaches(testArea?: TestArea): Observable<any> {
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/admin/clearAllCaches`);
  }

  getGlobalConfigs(testArea?: TestArea): Observable<any> {
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/admin/global-files/status`);
  }

  getOneLayerLookupTableConfigFiles(testArea?: TestArea): Observable<any> {
    return this.http.get(`${EnvConfigurationService.getDynamicTestUrl(testArea)}/api/v2/admin/lookup-table-files/status`);
  }

  getLogLines(pageIndex: number, pageSize: number): Observable<any> {
    const params = {
      excludeMessage: "DynamicImageSet",
      alternativeMessage: "Corvet",
      page: pageIndex.toString(),
      size: pageSize.toString(),
    };
    return this.http.get(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/log`, { params });
  }

  getDynamicImageLog(): Observable<any> {
    return this.http.get(
      `${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/getspecificlog?message=DynamicImageSet&alternativeMessage=Corvet`
    );
  }

  getAssetProfilesList(): Observable<any> {
    return this.http.get(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/dis/reprocessList`);
  }

  reprocessAssetProfile(disId: string): Observable<string> {
    return this.http.get(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/dis/${disId}/reprocess`, { responseType: "text" });
  }

  getBatchSummary(): Observable<any> {
    return this.http.get(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/getBatchSummary`);
  }

  copyDis(disId: string, targetDisId: string): Observable<any> {
    let parameters = new HttpParams();
    parameters = parameters.append("targetDisId", targetDisId);
    return this.http.get(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/dis/${disId}/copy`, { params: parameters });
  }

  renameDis(disId: string, targetDisId: string): Observable<any> {
    let parameters = new HttpParams();
    parameters = parameters.append("targetDisId", targetDisId);
    return this.http.get(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/dis/${disId}/rename`, { params: parameters });
  }

  deleteDis(disId: string): Observable<any> {
    return this.http.delete(`${EnvConfigurationService.configuration.API_BASE_URL}/api/v2/admin/dis/${disId}`);
  }
}
